import React, { useEffect, useState } from "react";
import { prepareImageForUpload } from "../../utils/imageUploadPolicy";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ImageUploadField from "../../components/ImageUploadField";
// THE ADDRESS FIELD (2026-10-23). Shared with the add-location form: it emits the street, city, state, ZIP and
// the point Google resolved for the chosen address, so this form can no longer save one without the other.
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import "../../shop_registration_styles.css";
import dayjs from "dayjs";
import SidebarLayout from "./SidebarLayout";
import ShopVisibilityStatus from "../../components/ShopVisibilityStatus";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  formatBusinessType,
  getProfessionalTypeLabel,
  capitalizeFirst,
} from "../../utils/formatters";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const buildNameChangeCooldownMessage = (payload) => {
  const cooldownDays = Number(payload?.cooldownDays || 0);
  const nextAllowedAtRaw = String(payload?.nextAllowedAt || "").trim();
  const nextAllowedAt = nextAllowedAtRaw ? new Date(nextAllowedAtRaw) : null;
  const hasValidNextAllowedAt =
    nextAllowedAt instanceof Date && !Number.isNaN(nextAllowedAt.getTime());

  if (cooldownDays > 0 && hasValidNextAllowedAt) {
    return `Shop name can only be changed every ${cooldownDays} day(s). You can change it again on ${nextAllowedAt.toLocaleString()}.`;
  }

  if (cooldownDays > 0) {
    return `Shop name can only be changed every ${cooldownDays} day(s).`;
  }

  return "Shop name change is temporarily unavailable. Please try again later.";
};

const LocationProfile = () => {
  const { shopId, slug } = useParams(); // Support both shopId and slug
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pros, setPros] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursError, setHoursError] = useState("");
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [photoCaption, setPhotoCaption] = useState("");
  const [maxPhotos, setMaxPhotos] = useState(6); // Default, will be fetched from settings
  const [replacePhotoIndex, setReplacePhotoIndex] = useState(null); // Track which photo is being replaced
  const [businessTypes, setBusinessTypes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [businessHours, setBusinessHours] = useState({
    monday: { open: "09:00", close: "18:00", active: true },
    tuesday: { open: "09:00", close: "18:00", active: true },
    wednesday: { open: "09:00", close: "18:00", active: true },
    thursday: { open: "09:00", close: "18:00", active: true },
    friday: { open: "09:00", close: "18:00", active: true },
    saturday: { open: "10:00", close: "16:00", active: true },
    sunday: { open: "10:00", close: "16:00", active: false },
  });

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [ratingDistribution, setRatingDistribution] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  });

  useEffect(() => {
    const fetchShop = async () => {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("ogestyle_token");
      try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/my-shops`;
        const res = await axios.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Find shop by slug or shopId
        const found = (res.data.shops || []).find((s) =>
          slug ? s.slug === slug : s._id === shopId,
        );
        if (!found) {
          throw new Error("Location not found");
        }
        setShop(found);
        // Use found shop's _id for subsequent API calls
        const actualShopId = found._id;

        // Load existing business hours if available
        if (found.schedule && typeof found.schedule === "object") {
          // Convert capitalized day names from database to lowercase for state
          const lowercaseHours = {};
          Object.keys(found.schedule).forEach((day) => {
            const lowercaseDay = day.toLowerCase();
            lowercaseHours[lowercaseDay] = found.schedule[day];
          });
          setBusinessHours(lowercaseHours);
        }

        // Fetch business types
        try {
          const typesRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || ""}/api/platform-settings/business-categories`,
          );
          setBusinessTypes(typesRes.data.types || []);
        } catch (err) {
        }

        // Fetch platform settings for max photos
        try {
          const settingsRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/platform-settings/public`,
          );
          setMaxPhotos(settingsRes.data.maxPhotosPerLocation || 3);
        } catch (err) {
        }

        // Fetch bookings for this location
        setBookingsLoading(true);
        setBookingsError("");
        try {
          const bookingsRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || ""}/api/bookings/location/${actualShopId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          setBookings(bookingsRes.data.bookings || []);
        } catch (err) {
          setBookingsError("");
          setBookings([]);
        } finally {
          setBookingsLoading(false);
        }

        // Fetch reviews for this location
        setReviewsLoading(true);
        try {
          const reviewsRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || ""}/api/bookings/location/${actualShopId}/reviews?limit=20`,
          );
          setReviews(reviewsRes.data.reviews || []);
          setAverageRating(reviewsRes.data.averageRating || 0);
          setTotalReviews(reviewsRes.data.totalReviews || 0);
          setRatingDistribution(
            reviewsRes.data.ratingDistribution || {
              5: 0,
              4: 0,
              3: 0,
              2: 0,
              1: 0,
            },
          );
        } catch (err) {
          setReviews([]);
          setAverageRating(0);
          setTotalReviews(0);
          setRatingDistribution({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
        } finally {
          setReviewsLoading(false);
        }
      } catch (err) {
        setError("Could not load location details.");
      } finally {
        setLoading(false);
      }
    };
    fetchShop();
  }, [shopId]);

  useEffect(() => {
    const fetchPros = async () => {
      if (!shop) return;
      const token = localStorage.getItem("ogestyle_token");
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/${shop._id}/staff`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setPros(res.data.staff || []);
      } catch {
        setPros([]);
      }
    };
    fetchPros();
  }, [shop]);

  const handleSaveBusinessHours = async () => {
    setHoursLoading(true);
    setHoursError("");
    const token = localStorage.getItem("ogestyle_token");

    // Use shop._id instead of shopId from URL params (which could be a slug or undefined)
    const actualShopId = shop?._id;
    if (!actualShopId) {
      setHoursError("Shop ID not found. Please refresh the page.");
      setHoursLoading(false);
      return;
    }

    try {
      // Convert lowercase day names to capitalized format for database
      const capitalizedHours = {};
      Object.keys(businessHours).forEach((day) => {
        const capitalizedDay = day.charAt(0).toUpperCase() + day.slice(1);
        capitalizedHours[capitalizedDay] = businessHours[day];
      });

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/${actualShopId}/business-hours`,
        { businessHours: capitalizedHours },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update shop state with new schedule
      setShop((prev) => ({ ...prev, schedule: capitalizedHours }));
      setShowHoursModal(false);
      alert("Business hours saved successfully!");
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        "Failed to save business hours. Please try again.";
      setHoursError(errorMsg);
    } finally {
      setHoursLoading(false);
    }
  };

  if (loading)
    return (
      <SidebarLayout>
        <div className="dashboard-content locations-page">
          <p>Loading...</p>
        </div>
      </SidebarLayout>
    );
  if (error)
    return (
      <SidebarLayout>
        <div className="dashboard-content locations-page">
          <p style={{ color: "#b91c1c" }}>{error}</p>
        </div>
      </SidebarLayout>
    );
  if (!shop) return null;

  // Limit preview lists to keep page concise
  const previewBookings = bookings.slice(0, 10);
  const previewPros = pros.slice(0, 6); // Show 6 random professionals

  return (
    <SidebarLayout>
      <div
        className="dashboard-content locations-page"
        style={{
          maxWidth: "100%",
          width: "100%",
          margin: "0 auto",
          padding: "24px 12px",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 24,
            background: "none",
            border: "none",
            color: "#3b82f6",
            fontWeight: 600,
            fontSize: 15,
            cursor: "pointer",
          }}
        >
          &larr; Back
        </button>
        {/* Shop Info */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            padding: "24px 20px",
            marginBottom: 32,
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: "240px", maxWidth: "100%" }}>
              <h1
                style={{
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  fontSize: 22,
                  fontWeight: 700,
                  margin: 0,
                }}
              >
                {shop.name}
              </h1>
              <div
                style={{
                  margin: "10px 0 14px 0",
                  color: "#6b7280",
                  fontSize: 14,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {Array.isArray(shop.type)
                  ? shop.type.map(formatBusinessType).join(", ")
                  : formatBusinessType(shop.type)}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#374151",
                  marginBottom: 8,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {capitalizeFirst(shop.street)}
                <br />
                {capitalizeFirst(shop.city)}, {capitalizeFirst(shop.state)}{" "}
                {shop.zip}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  marginBottom: 8,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {shop.phone && <span>📞 {shop.phone} &nbsp; </span>}
                {shop.email && <span>✉️ {shop.email}</span>}
              </div>
              <div style={{ margin: "10px 0 0 0" }}>
                <span
                  style={{
                    padding: "6px 18px",
                    borderRadius: "16px",
                    background: shop.approved ? "#ecfdf5" : "#fef3c7",
                    color: shop.approved ? "#059669" : "#92400e",
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {shop.approved ? "✓ Active" : "⏳ Pending Approval"}
                </span>
              </div>
            </div>
            <div
              style={{
                flex: "0 0 auto",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              <div
                style={{
                  background: "#f3f4f6",
                  borderRadius: "50%",
                  width: 64,
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 24,
                  color: "#3b82f6",
                  margin: "0 auto 10px auto",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
                title="Number of professionals at this location"
              >
                {pros.length}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Professionals
              </div>
            </div>
          </div>{" "}
          <div
            style={{
              marginTop: 20,
              paddingTop: 20,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => {
                setEditForm({
                  name: shop.name || "",
                  type: Array.isArray(shop.type)
                    ? shop.type
                    : shop.type
                      ? [shop.type]
                      : [],
                  street: shop.street || "",
                  city: shop.city || "",
                  state: shop.state || "",
                  zipCode: shop.zipCode || "",
                  phone: shop.phone || "",
                  email: shop.email || "",
                  /**
                   * DELIBERATELY NULL, NOT THE SHOP'S CURRENT POINT (2026-10-23).
                   *
                   * The server treats a `lat`/`lng` in the body as authoritative. Seeding them from the saved
                   * record would mean that editing the street by hand re-submitted the OLD coordinates beside
                   * the NEW address — the exact disagreement the geocode-integrity work exists to prevent. Left
                   * null, the server geocodes whatever address arrives; filled in only when the person picks a
                   * suggestion, in which case Google's point is the right one.
                   */
                  lat: null,
                  lng: null,
                });
                setShowEditModal(true);
              }}
              style={{
                width: "100%",
                background: "#3b82f6",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              Edit Shop
            </button>
          </div>{" "}
        </div>

        {/* Shop Visibility Status - Full Detail */}
        <div style={{ marginBottom: 24 }}>
          <ShopVisibilityStatus shop={shop} compact={false} />
        </div>

        {/* Business Hours Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            padding: "24px 20px",
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              margin: "0 0 16px 0",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            🕒 Business Hours
          </h2>
          <div style={{ display: "grid", gap: "12px" }}>
            {Object.entries(businessHours).map(([day, hours]) => (
              <div
                key={day}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px",
                  background: hours.active ? "#f9fafb" : "#fee2e2",
                  borderRadius: "8px",
                  border: `1px solid ${hours.active ? "#e5e7eb" : "#fecaca"}`,
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    textTransform: "capitalize",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                    minWidth: "100px",
                  }}
                >
                  {day}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                    color: hours.active ? "#374151" : "#dc2626",
                  }}
                >
                  {hours.active ? `${hours.open} - ${hours.close}` : "Closed"}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowHoursModal(true)}
            style={{
              marginTop: "16px",
              width: "100%",
              background: "#f3f4f6",
              color: "#374151",
              border: "none",
              borderRadius: 8,
              padding: "10px 18px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Edit Hours
          </button>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: window.innerWidth <= 768 ? "20px" : 32,
                minWidth: window.innerWidth <= 768 ? "auto" : 340,
                maxWidth: 600,
                width: "100%",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                position: "relative",
                maxHeight: "90vh",
                overflow: "auto",
                boxSizing: "border-box",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Edit Location Details
              </h2>
              {editError && (
                <div style={{ color: "#b91c1c", marginBottom: 12 }}>
                  {editError}
                </div>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setEditLoading(true);
                  setEditError("");
                  const token = localStorage.getItem("ogestyle_token");
                  try {
                    // TODO: Replace with backend API call
                    await axios.put(
                      `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/${shop._id}`,
                      editForm,
                      {
                        headers: { Authorization: `Bearer ${token}` },
                      },
                    );
                    setShowEditModal(false);
                    // Refetch shop info
                    window.location.reload();
                  } catch (err) {
                    const payload = err?.response?.data || {};
                    if (payload?.error === "name_change_cooldown_active") {
                      setEditError(buildNameChangeCooldownMessage(payload));
                    } else {
                      setEditError(payload?.message || "Failed to update location.");
                    }
                  } finally {
                    setEditLoading(false);
                  }
                }}
              >
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 15 }}>Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      fontSize: 15,
                      marginTop: 4,
                      boxSizing: "border-box",
                    }}
                    required
                  />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      fontWeight: 600,
                      fontSize: 15,
                      display: "block",
                      marginBottom: 8,
                    }}
                  >
                    Type (select all that apply)
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        window.innerWidth <= 768 ? "1fr" : "repeat(2, 1fr)",
                      gap: "12px",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      background: "#f9fafb",
                      maxHeight: "200px",
                      overflow: "auto",
                    }}
                  >
                    {businessTypes.map((type) => (
                      <label
                        key={type.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        <input
                          type="checkbox"
                          value={type.value}
                          checked={editForm.type.includes(type.value)}
                          onChange={(e) => {
                            const { value, checked } = e.target;
                            setEditForm((prev) => ({
                              ...prev,
                              type: checked
                                ? [...prev.type, value]
                                : prev.type.filter((t) => t !== value),
                            }));
                          }}
                          style={{ marginRight: "8px" }}
                        />
                        {type.emoji} {type.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  {/* WHY THIS REPLACED A PLAIN INPUT (2026-10-23). The shop's coordinates are what every
                      proximity search uses, and this form collected a street with no suggestions and posted it
                      with no coordinates at all — so a moved shop kept its original point for ever and became
                      invisible to customers nearby. Picking the address now supplies the point as well. */}
                  <label style={{ fontWeight: 600, fontSize: 15 }}>Street</label>
                  <AddressAutocompleteInput
                    value={editForm.street}
                    onChange={(street) =>
                      setEditForm((f) => ({ ...f, street }))
                    }
                    onAddressSelect={(address) =>
                      setEditForm((f) => ({
                        ...f,
                        street: address.street || f.street,
                        city: address.city || f.city,
                        state: address.state || f.state,
                        zipCode: address.zip || f.zipCode,
                        // THE POINT, from the same place as the parts above. `null` when Google gave none, so
                        // the server geocodes the typed address instead of trusting a guess.
                        lat: Number.isFinite(address.lat) ? address.lat : null,
                        lng: Number.isFinite(address.lng) ? address.lng : null,
                      }))
                    }
                    placeholder="123 Main St"
                    inputStyle={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      fontSize: 15,
                      marginTop: 4,
                      boxSizing: "border-box",
                    }}
                  />
                  <p style={{ margin: "6px 0 0 0", fontSize: 12, color: "#6b7280" }}>
                    Pick the address from the list so the map pin moves with it.
                  </p>
                </div>
                <div
                  style={{
                    marginBottom: 14,
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label style={{ fontWeight: 600, fontSize: 15 }}>
                      City
                    </label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, city: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #d1d5db",
                        fontSize: 15,
                        marginTop: 4,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: 15 }}>
                      State
                    </label>
                    <input
                      type="text"
                      value={editForm.state}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, state: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #d1d5db",
                        fontSize: 15,
                        marginTop: 4,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: 15 }}>ZIP</label>
                    <input
                      type="text"
                      value={editForm.zipCode}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, zipCode: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #d1d5db",
                        fontSize: 15,
                        marginTop: 4,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600, fontSize: 15 }}>Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      fontSize: 15,
                      marginTop: 4,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontWeight: 600, fontSize: 15 }}>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, email: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #d1d5db",
                      fontSize: 15,
                      marginTop: 4,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    style={{
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 18px",
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    style={{
                      background: "#3b82f6",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 18px",
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: editLoading ? "not-allowed" : "pointer",
                      opacity: editLoading ? 0.7 : 1,
                    }}
                  >
                    {editLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Business Hours Modal */}
        {showHoursModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: window.innerWidth <= 768 ? "20px" : 32,
                maxWidth: 550,
                width: "100%",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                position: "relative",
                maxHeight: "90vh",
                overflow: "auto",
                boxSizing: "border-box",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                Edit Business Hours
              </h2>

              {/* Quick Set All Days Section */}
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e40af",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  ⚡ Quick Set All Days
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    gap: "8px",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: "500",
                        marginBottom: "6px",
                        color: "#374151",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      Open
                    </label>
                    <input
                      type="time"
                      id="quickSetOpen"
                      defaultValue="09:00"
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "13px",
                        fontWeight: "500",
                        marginBottom: "6px",
                        color: "#374151",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                      }}
                    >
                      Close
                    </label>
                    <input
                      type="time"
                      id="quickSetClose"
                      defaultValue="18:00"
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        fontFamily:
                          "var(--font-family-primary, Inter, sans-serif)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const openTime =
                        document.getElementById("quickSetOpen").value;
                      const closeTime =
                        document.getElementById("quickSetClose").value;
                      setBusinessHours((prev) => {
                        const updated = {};
                        Object.keys(prev).forEach((day) => {
                          updated[day] = {
                            open: openTime,
                            close: closeTime,
                            active: true,
                          };
                        });
                        return updated;
                      });
                    }}
                    style={{
                      background: "#2563eb",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    Apply to All
                  </button>
                </div>
                <p
                  style={{
                    margin: "10px 0 0 0",
                    fontSize: "12px",
                    color: "#6b7280",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Set the same hours for all days, then customize individual
                  days below.
                </p>
              </div>

              <div
                style={{ display: "grid", gap: "16px", marginBottom: "24px" }}
              >
                {Object.entries(businessHours).map(([day, hours]) => (
                  <div
                    key={day}
                    style={{
                      padding: "16px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        style={{
                          fontWeight: "600",
                          fontSize: "15px",
                          textTransform: "capitalize",
                          fontFamily:
                            "var(--font-family-primary, Inter, sans-serif)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={hours.active}
                          onChange={(e) =>
                            setBusinessHours((prev) => ({
                              ...prev,
                              [day]: { ...prev[day], active: e.target.checked },
                            }))
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        {day}
                      </label>
                      {hours.active && (
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          Open
                        </span>
                      )}
                    </div>
                    {hours.active && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "13px",
                              fontWeight: "500",
                              marginBottom: "6px",
                              color: "#6b7280",
                              fontFamily:
                                "var(--font-family-primary, Inter, sans-serif)",
                            }}
                          >
                            Opening Time
                          </label>
                          <input
                            type="time"
                            value={hours.open}
                            onChange={(e) =>
                              setBusinessHours((prev) => ({
                                ...prev,
                                [day]: { ...prev[day], open: e.target.value },
                              }))
                            }
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "6px",
                              border: "1px solid #d1d5db",
                              fontSize: "14px",
                              fontFamily:
                                "var(--font-family-primary, Inter, sans-serif)",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "13px",
                              fontWeight: "500",
                              marginBottom: "6px",
                              color: "#6b7280",
                              fontFamily:
                                "var(--font-family-primary, Inter, sans-serif)",
                            }}
                          >
                            Closing Time
                          </label>
                          <input
                            type="time"
                            value={hours.close}
                            onChange={(e) =>
                              setBusinessHours((prev) => ({
                                ...prev,
                                [day]: { ...prev[day], close: e.target.value },
                              }))
                            }
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "6px",
                              border: "1px solid #d1d5db",
                              fontSize: "14px",
                              fontFamily:
                                "var(--font-family-primary, Inter, sans-serif)",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {hoursError && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    background: "#fef2f2",
                    borderRadius: 8,
                    color: "#b91c1c",
                    fontSize: 14,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {hoursError}
                </div>
              )}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowHoursModal(false);
                    setHoursError("");
                  }}
                  disabled={hoursLoading}
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 20px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: hoursLoading ? "not-allowed" : "pointer",
                    opacity: hoursLoading ? 0.5 : 1,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveBusinessHours}
                  disabled={hoursLoading}
                  style={{
                    background: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "10px 20px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: hoursLoading ? "not-allowed" : "pointer",
                    opacity: hoursLoading ? 0.7 : 1,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {hoursLoading ? "Saving..." : "Setup Shop Business Hours"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            padding: "20px 16px",
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              margin: "0 0 16px 0",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Upcoming & Recent Bookings
          </h2>
          {bookingsLoading ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              Loading bookings...
            </p>
          ) : bookingsError ? (
            <p
              style={{
                color: "#b91c1c",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              {bookingsError}
            </p>
          ) : bookings.length === 0 ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              No bookings found for this location.
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f3f4f6",
                      color: "#374151",
                      fontWeight: 600,
                    }}
                  >
                    <th
                      style={{
                        padding: "10px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: "10px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Time
                    </th>
                    <th
                      style={{
                        padding: "10px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Service
                    </th>
                    <th
                      style={{
                        padding: "10px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewBookings.map((b) => (
                    <tr
                      key={b._id}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td style={{ padding: "8px 8px" }}>
                        {dayjs(b.appointmentDate).format("M/D/YY")}
                      </td>
                      <td style={{ padding: "8px 8px" }}>{b.startTime}</td>
                      <td style={{ padding: "8px 8px" }}>
                        {b.serviceId?.name || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "8px 8px",
                          color:
                            b.status === "confirmed"
                              ? "#059669"
                              : b.status === "pending"
                                ? "#f59e0b"
                                : b.status === "completed"
                                  ? "#2563eb"
                                  : "#b91c1c",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {b.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Footer: show summary and link to full bookings page */}
          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: 14,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              Showing {Math.min(bookings.length, 10)} of {bookings.length}{" "}
              bookings
            </div>
            <div>
              <button
                onClick={() =>
                  navigate(`/dashboard/bookings?shopId=${shop._id}`)
                }
                className="btn-secondary"
                style={{ padding: "8px 12px", fontSize: 14 }}
              >
                View all bookings
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Summary */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
            marginBottom: 32,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                color: "#6b7280",
                marginBottom: 8,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              This week
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {
                    bookings.filter((b) =>
                      dayjs(b.appointmentDate).isAfter(
                        dayjs().subtract(7, "day"),
                      ),
                    ).length
                  }
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Bookings</div>
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  $
                  {bookings
                    .reduce(
                      (s, b) =>
                        s +
                        ((b.servicePriceAtBooking || 0) +
                          (b.productsTotalPrice || 0)),
                      0,
                    )
                    .toFixed(2)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Total (all time)
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                color: "#6b7280",
                marginBottom: 8,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              Revenue (last 7 days)
            </div>
            {/* Prepare chart data */}
            {(() => {
              const labels = [];
              const dataPoints = [];
              for (let i = 6; i >= 0; i--) {
                const day = dayjs().subtract(i, "day");
                labels.push(day.format("MMM D"));
                const total = bookings.reduce((sum, b) => {
                  const bDate = dayjs(b.appointmentDate);
                  if (bDate.isSame(day, "day")) {
                    return (
                      sum +
                      ((b.servicePriceAtBooking || 0) +
                        (b.productsTotalPrice || 0))
                    );
                  }
                  return sum;
                }, 0);
                dataPoints.push(Number(total.toFixed(2)));
              }

              const chartData = {
                labels,
                datasets: [
                  {
                    label: "Revenue ($)",
                    data: dataPoints,
                    backgroundColor: "#3b82f6",
                  },
                ],
              };

              const options = {
                responsive: true,
                plugins: { legend: { display: false } },
                scales: {
                  x: { grid: { display: false } },
                  y: { ticks: { callback: (v) => `$${v}` } },
                },
              };

              return <Bar data={chartData} options={options} />;
            })()}
          </div>
        </div>

        {/* Professionals Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            padding: 28,
          }}
        >
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: "0 0 18px 0",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Professionals at this Location
          </h2>
          {pros.length === 0 ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              No professionals found for this location.
            </p>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(min(100%, 280px), 1fr))",
                  gap: 18,
                }}
              >
                {previewPros.map((pro) => (
                  <div
                    key={pro._id}
                    style={{
                      background: "#f9fafb",
                      borderRadius: 12,
                      padding: 18,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          background: "#e0e7ef",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 18,
                          color: "#3b82f6",
                          fontFamily:
                            "var(--font-family-primary, Inter, sans-serif)",
                        }}
                      >
                        {pro.userId?.firstName?.[0] || ""}
                        {pro.userId?.lastName?.[0] || ""}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                            color: "#374151",
                          }}
                        >
                          {pro.userId?.firstName} {pro.userId?.lastName}
                        </div>
                        <div
                          style={{
                            color: "#6b7280",
                            fontSize: 13,
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                            marginTop: 2,
                          }}
                        >
                          {getProfessionalTypeLabel(pro)}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "4px 10px",
                          borderRadius: 12,
                          background:
                            pro.status === "Active" ? "#ecfdf5" : "#fef3c7",
                          color:
                            pro.status === "Active" ? "#059669" : "#92400e",
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily:
                            "var(--font-family-primary, Inter, sans-serif)",
                        }}
                      >
                        {pro.status}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        onClick={() =>
                          navigate(`/dashboard/professionals/${pro._id}`)
                        }
                        style={{
                          flex: 1,
                          padding: "6px 12px",
                          background: "#3b82f6",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily:
                            "var(--font-family-primary, Inter, sans-serif)",
                        }}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 18,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Showing {Math.min(pros.length, 6)} of {pros.length}{" "}
                  professionals
                </div>
                <div>
                  <button
                    onClick={() =>
                      navigate(`/dashboard/professionals?shopId=${shop._id}`)
                    }
                    style={{
                      padding: "8px 16px",
                      fontSize: 14,
                      background: "#3b82f6",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    View all professionals
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Photo Gallery Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            padding: "24px 20px",
            marginTop: 32,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 18,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  margin: 0,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                Photos
              </h2>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: 13,
                  margin: "4px 0 0",
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {shop.images?.length || 0} / {maxPhotos} photos
              </p>
            </div>
            <button
              onClick={() => {
                setReplacePhotoIndex(null);
                setShowPhotoModal(true);
              }}
              disabled={shop.images?.length >= maxPhotos}
              style={{
                background:
                  shop.images?.length >= maxPhotos ? "#d1d5db" : "#3b82f6",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 18px",
                fontWeight: 600,
                fontSize: 15,
                cursor:
                  shop.images?.length >= maxPhotos ? "not-allowed" : "pointer",
                opacity: shop.images?.length >= maxPhotos ? 0.6 : 1,
              }}
            >
              {shop.images?.length >= maxPhotos ? "Limit Reached" : "Add Photo"}
            </button>
          </div>
          {!shop.images || shop.images.length === 0 ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              No photos yet. Add some to showcase your location!
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(min(100%, 120px), 1fr))",
                gap: 12,
              }}
            >
              {shop.images.map((img, idx) => {
                // Handle both old format (string) and new format (object)
                const imageUrl = typeof img === "string" ? img : img?.url || "";
                const imageCaption =
                  typeof img === "object" && img !== null ? img.caption : "";

                // Debug: Log image data to console
                if (!imageUrl) {
                }

                return (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div
                      style={{ position: "relative", paddingBottom: "100%" }}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={imageCaption || `Shop ${idx + 1}`}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#9ca3af",
                            fontSize: 12,
                          }}
                        >
                          No image
                        </div>
                      )}
                      <div
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          display: "flex",
                          gap: 6,
                        }}
                      >
                        <button
                          onClick={() => {
                            setReplacePhotoIndex(idx);
                            setShowPhotoModal(true);
                          }}
                          style={{
                            background: "rgba(59,130,246,0.95)",
                            border: "none",
                            borderRadius: 6,
                            padding: "4px 8px",
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Replace
                        </button>
                      </div>
                    </div>
                    {imageCaption && (
                      <div
                        style={{
                          padding: "8px 10px",
                          background: "#f9fafb",
                          borderTop: "1px solid #e5e7eb",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: 11,
                            color: "#374151",
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {imageCaption}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Photo Upload Modal */}
        {showPhotoModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.18)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 32,
                minWidth: 340,
                maxWidth: 420,
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                position: "relative",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                }}
              >
                {replacePhotoIndex !== null ? "Replace Photo" : "Upload Photo"}
              </h2>
              {replacePhotoIndex !== null && (
                <div
                  style={{
                    background: "#fef3c7",
                    border: "1px solid #fbbf24",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 18,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      color: "#92400e",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    ⚠️ The old photo will be permanently deleted when you upload
                    the new one.
                  </p>
                </div>
              )}
              <div style={{ marginBottom: 18 }}>
                <ImageUploadField
                  variant="inline"
                  label={replacePhotoIndex !== null ? "New Photo" : "Photo"}
                  buttonLabel="Choose Photo"
                  uploadOnSelect={false}
                  showPreviews={false}
                  onSelectFiles={(file) => {
                    if (!file) return;
                    setSelectedPhoto(file);
                    setPhotoPreview(URL.createObjectURL(file));
                  }}
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#374151",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  placeholder="Add a description..."
                  maxLength={200}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                    fontSize: 14,
                  }}
                />
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: 12,
                    color: "#6b7280",
                    fontFamily: "var(--font-family-primary, Inter, sans-serif)",
                  }}
                >
                  {photoCaption.length}/200 characters
                </p>
              </div>
              {photoPreview && (
                <div style={{ marginBottom: 18 }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      maxHeight: 300,
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowPhotoModal(false);
                    setSelectedPhoto(null);
                    setPhotoPreview(null);
                    setPhotoCaption("");
                    setReplacePhotoIndex(null);
                  }}
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selectedPhoto) return;
                    setUploadLoading(true);
                    const token = localStorage.getItem("ogestyle_token");
                    const formData = new FormData();
                    formData.append("image", await prepareImageForUpload(selectedPhoto));
                    formData.append("caption", photoCaption);
                    try {
                      let response;
                      if (replacePhotoIndex !== null) {
                        // Replace existing photo
                        response = await axios.put(
                          `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/${shop._id}/photos/${replacePhotoIndex}`,
                          formData,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "multipart/form-data",
                            },
                          },
                        );
                      } else {
                        // Upload new photo
                        response = await axios.post(
                          `${process.env.REACT_APP_API_BASE_URL || ""}/api/shops/${shop._id}/upload-photo`,
                          formData,
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "multipart/form-data",
                            },
                          },
                        );
                      }
                      // Update shop state with new data including uploaded photo
                      setShop(response.data.shop);
                      setShowPhotoModal(false);
                      setSelectedPhoto(null);
                      setPhotoPreview(null);
                      setPhotoCaption("");
                      setReplacePhotoIndex(null);
                    } catch (err) {
                      alert(
                        err.response?.data?.message ||
                          "Failed to upload photo.",
                      );
                    } finally {
                      setUploadLoading(false);
                    }
                  }}
                  disabled={!selectedPhoto || uploadLoading}
                  style={{
                    background:
                      selectedPhoto && !uploadLoading ? "#3b82f6" : "#d1d5db",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: 15,
                    cursor:
                      selectedPhoto && !uploadLoading
                        ? "pointer"
                        : "not-allowed",
                    opacity: selectedPhoto && !uploadLoading ? 1 : 0.7,
                  }}
                >
                  {uploadLoading
                    ? replacePhotoIndex !== null
                      ? "Replacing..."
                      : "Uploading..."
                    : replacePhotoIndex !== null
                      ? "Replace"
                      : "Upload"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reviews & Ratings Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            padding: "24px 20px",
            marginTop: 32,
          }}
        >
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              margin: "0 0 18px 0",
              fontFamily: "var(--font-family-primary, Inter, sans-serif)",
            }}
          >
            Reviews & Ratings
          </h2>
          {reviewsLoading ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              Loading reviews...
            </p>
          ) : totalReviews === 0 ? (
            <p
              style={{
                color: "#6b7280",
                fontSize: 15,
                fontFamily: "var(--font-family-primary, Inter, sans-serif)",
              }}
            >
              No reviews yet. Be the first to leave a review!
            </p>
          ) : (
            <>
              {/* Rating Summary */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
                  gap: 24,
                  marginBottom: 32,
                  padding: 20,
                  background: "#f9fafb",
                  borderRadius: 12,
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: 56,
                      fontWeight: 700,
                      color: "#3b82f6",
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    {averageRating.toFixed(1)}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      color: "#374151",
                      marginTop: 8,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    {"⭐".repeat(Math.round(averageRating))}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#6b7280",
                      marginTop: 8,
                      fontFamily:
                        "var(--font-family-primary, Inter, sans-serif)",
                    }}
                  >
                    {totalReviews} {totalReviews === 1 ? "review" : "reviews"}
                  </div>
                </div>
                <div>
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = ratingDistribution[star] || 0;
                    const percentage =
                      totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    return (
                      <div
                        key={star}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginBottom: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            width: 60,
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {star} stars
                        </span>
                        <div
                          style={{
                            flex: 1,
                            height: 10,
                            background: "#e5e7eb",
                            borderRadius: 5,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${percentage}%`,
                              height: "100%",
                              background: "#3b82f6",
                              transition: "width 0.3s",
                            }}
                          ></div>
                        </div>
                        <span
                          style={{
                            fontSize: 14,
                            color: "#6b7280",
                            width: 40,
                            textAlign: "right",
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reviews List */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                {reviews.slice(0, 10).map((review) => (
                  <div
                    key={review._id}
                    style={{
                      padding: 20,
                      background: "#f9fafb",
                      borderRadius: 12,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: "#374151",
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {/* First name only, matching the other public review lists.
                              This one used to print the full name and had no guest
                              fallback, so it showed a surname or "Anonymous". */}
                          {review.customerName ||
                            review.customerId?.firstName ||
                            "Anonymous"}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: "#6b7280",
                            marginTop: 4,
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {review.serviceId?.name &&
                            `Service: ${review.serviceId.name}`}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 18 }}>
                          {"⭐".repeat(review.rating)}
                        </span>
                        <span
                          style={{
                            fontSize: 14,
                            color: "#6b7280",
                            fontFamily:
                              "var(--font-family-primary, Inter, sans-serif)",
                          }}
                        >
                          {dayjs(review.reviewedAt).format("MMM D, YYYY")}
                        </span>
                      </div>
                    </div>
                    {review.review && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 15,
                          color: "#374151",
                          lineHeight: 1.6,
                          fontFamily:
                            "var(--font-family-primary, Inter, sans-serif)",
                        }}
                      >
                        "{review.review}"
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {reviews.length > 10 && (
                <div style={{ marginTop: 20, textAlign: "center" }}>
                  <button
                    onClick={() => {
                      /* TODO: Load more reviews or show all */
                    }}
                    className="btn-secondary"
                    style={{ padding: "8px 16px", fontSize: 14 }}
                  >
                    Show more reviews
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default LocationProfile;
